import React, { useState, useRef, useEffect } from 'react';

// Define LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Define grouped area combinations
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    value: 'KIAWAH_SEABROOK',
    label: 'Kiawah + Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND],
    isGroup: true
  },
  IOP_WILD_DUNES: {
    value: 'IOP_WILD_DUNES',
    label: 'Isle of Palms + Isle of Palms Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES],
    isGroup: true
  }
};

// Available areas array with grouped options at the top and individual areas below
const AVAILABLE_AREAS = [
  // Grouped options at the top
  AREA_GROUPS.KIAWAH_SEABROOK,
  AREA_GROUPS.IOP_WILD_DUNES,
  // Divider
  { isDivider: true },
  // Individual areas
  { value: LocationPreference.KIAWAH, label: 'Kiawah', isGroup: false },
  { value: LocationPreference.ISLE_OF_PALMS, label: 'Isle of Palms', isGroup: false },
  { value: LocationPreference.ISLE_OF_PALMS_WILD_DUNES, label: 'Isle of Palms - Wild Dunes', isGroup: false },
  { value: LocationPreference.SEABROOK_ISLAND, label: 'Seabrook Island', isGroup: false }
];

const PayrollAreaSelector = ({ selectedAreas = [], onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to check if a group is fully selected
  const isGroupSelected = (group) => {
    return group.areas.every(area => selectedAreas.includes(area));
  };

  // Helper function to check if a group is partially selected
  const isGroupPartiallySelected = (group) => {
    return group.areas.some(area => selectedAreas.includes(area)) && !isGroupSelected(group);
  };

  const handleAreaToggle = (areaValue) => {
    // Check if this is a grouped selection
    const selectedGroup = Object.values(AREA_GROUPS).find(group => group.value === areaValue);
    
    if (selectedGroup) {
      // Handle group selection
      const isCurrentlySelected = isGroupSelected(selectedGroup);
      
      if (isCurrentlySelected) {
        // Deselect all areas in the group
        const newAreas = selectedAreas.filter(area => !selectedGroup.areas.includes(area));
        onChange(newAreas);
      } else {
        // Select all areas in the group (merge with existing selections)
        const newAreas = [...new Set([...selectedAreas, ...selectedGroup.areas])];
        onChange(newAreas);
      }
    } else {
      // Handle individual area selection
      if (selectedAreas.includes(areaValue)) {
        onChange(selectedAreas.filter(area => area !== areaValue));
      } else {
        onChange([...selectedAreas, areaValue]);
      }
    }
  };

  const handleClearAll = () => {
    onChange([]);
    setIsOpen(false);
  };

  const handleSelectAll = () => {
    // Select all individual areas
    const allIndividualAreas = AVAILABLE_AREAS
      .filter(area => !area.isGroup && !area.isDivider)
      .map(area => area.value);
    onChange(allIndividualAreas);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (selectedAreas.length === 0) {
      return 'All areas';
    }

    // Get selected groups for display
    const selectedGroups = Object.values(AREA_GROUPS).filter(group => isGroupSelected(group));
    
    // Get selected individual areas that aren't part of selected groups
    const selectedIndividualAreas = AVAILABLE_AREAS
      .filter(area => !area.isGroup && !area.isDivider && selectedAreas.includes(area.value))
      .filter(area => !selectedGroups.some(group => group.areas.includes(area.value)));

    // Display logic
    if (selectedGroups.length > 0) {
      const firstItem = selectedGroups[0].label;
      const totalSelections = selectedGroups.length + selectedIndividualAreas.length;
      
      if (totalSelections === 1) {
        return firstItem;
      }
      return `${firstItem} +${totalSelections - 1}`;
    }
    
    if (selectedIndividualAreas.length === 1) {
      return selectedIndividualAreas[0].label;
    }
    
    // Check if all areas are selected
    const allIndividualAreas = AVAILABLE_AREAS.filter(area => !area.isGroup && !area.isDivider);
    if (selectedAreas.length === allIndividualAreas.length) {
      return 'All areas';
    }
    
    return `${selectedAreas.length} areas selected`;
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Filter by Areas
      </label>
      
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-sky-500 focus:ring-sky-500 text-left bg-white hover:bg-gray-50"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-900">{getDisplayText()}</span>
            <svg
              className={`h-4 w-4 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
            {/* Quick action buttons */}
            <div className="border-b border-gray-200 px-3 py-2">
              <div className="flex justify-between gap-2">
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex-1 px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                >
                  Clear All
                </button>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex-1 px-2 py-1 text-xs font-medium text-sky-600 hover:text-sky-800 hover:bg-sky-50 rounded"
                >
                  Select All
                </button>
              </div>
            </div>

            {/* Area options */}
            {AVAILABLE_AREAS.map((area, index) => {
              if (area.isDivider) {
                return (
                  <div key={`divider-${index}`} className="border-t border-gray-200 my-1">
                    <div className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50">
                      Individual Areas
                    </div>
                  </div>
                );
              }

              const isSelected = area.isGroup ? isGroupSelected(area) : selectedAreas.includes(area.value);
              const isPartiallySelected = area.isGroup ? isGroupPartiallySelected(area) : false;
              
              return (
                <div
                  key={area.value}
                  className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${
                    isSelected ? (area.isGroup ? 'bg-green-50' : 'bg-sky-50') : 
                    isPartiallySelected ? 'bg-yellow-50' : ''
                  }`}
                  onClick={() => handleAreaToggle(area.value)}
                >
                  <div className="flex items-center">
                    <span className={`block truncate text-sm ${area.isGroup ? 'font-medium' : ''}`}>
                      {area.isGroup && '🔗 '}{area.label}
                      {area.isGroup && (
                        <span className="text-xs text-gray-500 font-normal ml-1">
                          ({area.areas.length} areas)
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                        area.isGroup ? 'text-green-600' : 'text-sky-600'
                      }`}>
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    )}
                    {isPartiallySelected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-yellow-600">
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Helper text */}
      <p className="text-xs text-gray-500">
        {selectedAreas.length === 0 
          ? 'Report will include all areas' 
          : `Report will be filtered to ${selectedAreas.length} selected area${selectedAreas.length !== 1 ? 's' : ''}`
        }
      </p>
    </div>
  );
};

export default PayrollAreaSelector;